import { useAccount } from "@/context/account";
import { gql } from "@/gql";
import {
  SortEnumType,
  UserNotificationStatus,
  NotificationType,
  type ListNotificationsFragment,
} from "@/gql/graphql";
import { gridToGql } from "@/utils/datagridToGql";
import { handleApprovalStatusNotification } from "@/utils/handleNotificationReceived";
import {
  useLazyQuery,
  useMutation,
  useQuery,
  useSubscription,
} from "@apollo/client/react";
import { uniqBy } from "lodash-es";

const MARK_AS_READ = gql(`
  mutation MarkAsRead($accountId: ID!, $notificationId: ID!) {
    markAsRead(accountId: $accountId, notificationId: $notificationId) {
      hasUnReadNotifications
      notification {
        ...ListNotifications
      }
    }
  }`);

const HAS_UNREAD_NOTIFICATIONS = gql(`
  query HasUnreadNotifications($accountId: ID!) {
    notifications(accountId: $accountId, first: 1) {
      hasUnReadNotifications
    }
  }`);

const NOTIFICATIONS = gql(`
  query Notifications($accountId: ID!, $after: String, $first: Int!, $order: [NotificationSortInput!]) {
    notifications(accountId: $accountId, after: $after, first: $first, order: $order) {
      hasUnReadNotifications
      totalCount
      nodes {
        ...ListNotifications
      }
    }
  }`);

const MARK_ALL_AS_READ = gql(`
  mutation MarkAllAsRead($accountId: ID!) {
    markAllAsRead(accountId: $accountId)
  }`);

const NOTIFICATIONS_SUBSCRIPTION = gql(`
  subscription SubscribeToNotifications($accountId: ID!) {
    notificationReceived(accountId: $accountId) {
      ...ListNotifications
    }
  }`);

const isNotificationNodes = (
  value: unknown
): value is ListNotificationsFragment[] => Array.isArray(value);

/** Prepend a subscription notification; no-op insert when id already exists. */
const mergeReceivedNotification = (
  existing: unknown,
  newFeedItem: ListNotificationsFragment
): Record<string, unknown> => {
  const existingRecord =
    existing && typeof existing === "object" && !("ref" in existing)
      ? (existing as Record<string, unknown>)
      : undefined;
  const existingNodes = existingRecord?.nodes;
  const existingTotalCount =
    typeof existingRecord?.totalCount === "number"
      ? existingRecord.totalCount
      : 0;

  if (!isNotificationNodes(existingNodes)) {
    return {
      ...existingRecord,
      hasUnReadNotifications: true,
    };
  }

  if (existingNodes.some((node) => node.id === newFeedItem.id)) {
    return {
      ...existingRecord,
      hasUnReadNotifications: true,
    };
  }

  return {
    ...existingRecord,
    hasUnReadNotifications: true,
    nodes: [newFeedItem, ...existingNodes],
    totalCount: existingTotalCount + 1,
  };
};

const useNotificationsService = () => {
  const { account } = useAccount();
  const accountId = account.id;

  const { data: unreadData } = useQuery(HAS_UNREAD_NOTIFICATIONS, {
    variables: { accountId },
    skip: accountId === undefined,
    context: { skipGlobalError: true },
  });

  const notificationsQuery = useLazyQuery(NOTIFICATIONS);

  const [fetchRows, { data: listData, fetchMore }] = notificationsQuery;

  useSubscription(NOTIFICATIONS_SUBSCRIPTION, {
    variables: { accountId },
    skip: accountId === undefined,
    onData: ({ client, data: subscriptionData }) => {
      const newFeedItem = subscriptionData.data?.notificationReceived;
      if (!newFeedItem) return;

      client.cache.modify({
        fields: {
          notifications(existing) {
            return mergeReceivedNotification(existing, newFeedItem);
          },
        },
      });

      if (
        newFeedItem.notificationType === NotificationType.ApprovalRequest &&
        newFeedItem.data?.__typename === "ContractApprovalStatusNotification"
      ) {
        handleApprovalStatusNotification(newFeedItem.data);
      }
    },
  });

  const [markAllAsReadMutation] = useMutation(MARK_ALL_AS_READ);

  const markAllAsRead = async () => {
    await markAllAsReadMutation({
      variables: {
        accountId,
      },
      update: (cache) => {
        cache.modify({
          fields: {
            notifications(existing: Record<string, unknown>) {
              return {
                ...existing,
                hasUnReadNotifications: false,
              };
            },
          },
        });

        const allCache = cache.extract();
        Object.keys(allCache).forEach((key) => {
          if (key.startsWith("Notification:")) {
            cache.modify({
              id: key,
              fields: {
                notificationStatus() {
                  return UserNotificationStatus.Read;
                },
              },
            });
          }
        });
      },
    });
  };

  const [markAsReadMutation] = useMutation(MARK_AS_READ);

  const markAsRead = async (notificationId: string) => {
    await markAsReadMutation({
      variables: {
        accountId,
        notificationId,
      },
      update: (cache, { data }) => {
        cache.modify({
          fields: {
            notifications(existing: Record<string, unknown>) {
              return {
                ...existing,
                hasUnReadNotifications: data?.markAsRead.hasUnReadNotifications,
              };
            },
          },
        });
      },
    });
  };

  const fetchNotifications = async (start: number, end: number) => {
    const { after, first } = gridToGql.continuous(start, end);

    const isFirstPage = start === 0;

    if (!isFirstPage) {
      const { data } = await fetchMore({
        variables: {
          accountId,
          first,
          after,
          order: [
            {
              createdDateTime: SortEnumType.Desc,
            },
          ],
        },
        updateQuery: (prev, { fetchMoreResult }) => {
          if (!fetchMoreResult) return prev;

          const newNodes = fetchMoreResult.notifications?.nodes ?? [];
          const existingNodes = prev.notifications?.nodes ?? [];

          return {
            notifications: {
              ...prev.notifications,
              nodes: uniqBy([...existingNodes, ...newNodes], "id"),
              totalCount: fetchMoreResult.notifications?.totalCount,
              hasUnReadNotifications:
                fetchMoreResult.notifications?.hasUnReadNotifications,
            },
          };
        },
      });

      return data?.notifications;
    }

    const { data } = await fetchRows({
      variables: {
        accountId,
        first,
        after,
        order: [
          {
            createdDateTime: SortEnumType.Desc,
          },
        ],
      },
    });

    return data?.notifications;
  };

  return {
    hasUnReadNotifications:
      unreadData?.notifications?.hasUnReadNotifications === true ||
      listData?.notifications?.hasUnReadNotifications === true,
    notificationsQuery,
    markAllAsRead,
    markAsRead,
    fetchNotifications,
  };
};

export { useNotificationsService };
